import { Component, Injector, OnInit, OnDestroy, Input, AfterViewInit, ViewChild } from '@angular/core';
import { ThemesLayoutBaseComponent } from '../themes/themes-layout-base.component';
import { LinkedUserDto, MyProfileOutput, ProfileServiceProxy, UserLinkServiceProxy } from '@shared/service-proxies/service-proxies';
import { LinkedAccountService } from '@app/shared/layout/linked-account.service';
import { AbpMultiTenancyService, AbpSessionService } from 'abp-ng2-module';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { ImpersonationService } from '@app/admin/users/impersonation.service';
import { AppConsts } from '@shared/AppConsts';
import { DateTimeService } from '@app/shared/common/timing/date-time.service';
import { Router } from '@angular/router';
import { MyProfileComponent } from './my-profile/my-profile.component';
import { AppRoutes } from '@app/shared/routes/app-routes';
import { resolveProfileImageUrl } from '@shared/utils/profile-image.util';

@Component({
    standalone: false,
    selector: 'user-menu',
    templateUrl: './user-menu.component.html',
    styleUrls: ['./user-menu.component.scss']
})
export class UserMenuComponent extends ThemesLayoutBaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('topBarComponentData', { static: true }) topBarComponentData: MyProfileComponent;
    @Input() iconOnly = false;
    @Input() isDropup = false;
    @Input() togglerCssClass = 'btn btn-icon w-auto btn-clean d-flex align-items-center btn-md px-2';
    @Input() textCssClass = 'text-dark-50 font-weight-bolder font-size-base d-none d-md-inline mr-2 cursor-pointer';
    @Input() symbolCssClass = 'symbol symbol-35 symbol-light-success';
    @Input() symbolTextCssClass = 'symbol-label font-size-h5 font-weight-bold';
    @Input() customStyle = 'btn btn-icon btn-dropdown btn-clean mr-1 mt-0';

    usernameFirstLetter = '';

    profilePicture = '';
    imgPath: string;
    imgPath1: string;
    userRole: any;

    shownLoginName = '';
    tenancyName = '';
    userName = '';
    profileName = '';
    demoShow: boolean;
    recentlyLinkedUsers: LinkedUserDto[];
    isImpersonatedLogin = false;
    isMultiTenancyEnabled = false;
    flag = false;
    mQuickUserOffcanvas: any;
    myProfileData: MyProfileOutput = new MyProfileOutput();
    uparrow = false;
    myProfileDataCheck: string;
    private readonly profilePictureChangedHandler = (imagePath?: string) => this.onProfilePictureChanged(imagePath);

    public constructor(
        private router: Router,
        injector: Injector,
        private _linkedAccountService: LinkedAccountService,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private _profileServiceProxy: ProfileServiceProxy,
        private _userLinkServiceProxy: UserLinkServiceProxy,
        private _authService: AppAuthService,
        private _impersonationService: ImpersonationService,
        private _abpSessionService: AbpSessionService,
        _dateTimeService: DateTimeService,
        private _profileService: ProfileServiceProxy
    ) {
        super(injector, _dateTimeService);
    }

    ngOnInit(): void {
        this.demoShow = false;
        this.imgPath = '';
        this.userRole = '';
        this.imgPath1 = '';
        this.isImpersonatedLogin = this._abpSessionService.impersonatorUserId > 0;
        this.isMultiTenancyEnabled = this._abpMultiTenancyService.isEnabled;
        this.setCurrentLoginInformations();
        this.myProfileInfo();
        this.usernameFirstLetter = this.appSession.user.userName.substring(0, 1).toUpperCase();
        abp.event.on('profilePictureChanged', this.profilePictureChangedHandler);
    }

    ngOnDestroy(): void {
        abp.event.off('profilePictureChanged', this.profilePictureChangedHandler);
    }

    ngAfterViewInit(): void {
    
        this.mQuickUserOffcanvas = new KTOffcanvas('kt_quick_user', {
            overlay: true,
            baseClass: 'offcanvas',
            placement: 'right',
            closeBy: 'kt_demo_panel_close',
            toggleBy: 'kt_quick_user_toggle'
        });

    }

    myProfileInfo() {
        let shouldRefresh = false;
        try {
            shouldRefresh = JSON.parse(localStorage.getItem('MyProfileDataSaved') || 'false');
        } catch {
            shouldRefresh = false;
        }

        const cachedProfileData = shouldRefresh ? null : this.getCachedProfileData();
        if (cachedProfileData) {
            this.applyProfileInfo(cachedProfileData);
            return;
        }

        this._profileService.myProfileInfo().subscribe(result => {
            AppConsts.myProfileInfo = result;
            localStorage.setItem('MyProfileData', JSON.stringify(result));
            localStorage.setItem('MyProfileDataSaved', 'false');
            this.applyProfileInfo(result);
        });
    }

    private getCachedProfileData(): MyProfileOutput | null {
        if (AppConsts.myProfileInfo) {
            return AppConsts.myProfileInfo;
        }

        const loggedUserData = localStorage.getItem('MyProfileData');
        if (!loggedUserData) {
            return null;
        }

        try {
            return JSON.parse(loggedUserData);
        } catch {
            return null;
        }
    }

    private applyProfileInfo(profileData: MyProfileOutput): void {
        this.myProfileData = profileData ? Object.assign(new MyProfileOutput(), profileData) : new MyProfileOutput();
        const storedImage = localStorage.getItem('imagePath');
        const image = resolveProfileImageUrl(storedImage, this.myProfileData.imagePath, AppConsts.loggedInUserImage);

        if (this.myProfileData.firstName && this.myProfileData.lastName) {
            this.profileName = this.myProfileData.firstName + ' ' + this.myProfileData.lastName;
        }

        if (Array.isArray(this.myProfileData.roleName)) {
            this.myProfileData.roleName = this.myProfileData.roleName.map(x => this.l(x));
        }

        this.imgPath = image;
        AppConsts.loggedInUserImage = image;
        this.markForCheck();
    }

    private onProfilePictureChanged(imagePath?: string): void {
        if (imagePath === undefined) {
            this.myProfileInfo();
            return;
        }

        const cachedProfileData = this.getCachedProfileData();
        if (cachedProfileData) {
            this.applyProfileInfo(cachedProfileData);
            return;
        }

        this.imgPath = resolveProfileImageUrl(imagePath);
        AppConsts.loggedInUserImage = this.imgPath;
        this.markForCheck();
    }

    setCurrentLoginInformations(): void {
        this.shownLoginName = this.appSession.getShownLoginName();
        this.tenancyName = this.appSession.tenancyName;
        this.userRole = this.appSession.user.userRole;
    }

    getShownUserName(linkedUser: LinkedUserDto): string {
        if (!this._abpMultiTenancyService.isEnabled) {
            return linkedUser.username;
        }

        return (linkedUser.tenantId ? linkedUser.tenancyName : '.') + '\\' + linkedUser.username;
    }
    getMyProfile() {
        this.router.navigate([AppRoutes.myProfile]);
    }

    myMethod() {
        
        this.flag = true;
    }

    getRecentlyLinkedUsers(): void {
        this._userLinkServiceProxy.getRecentlyUsedLinkedUsers().subscribe(result => {
            this.recentlyLinkedUsers = result.items;
        });
    }

    logout(): void {
        sessionStorage.clear();
        abp.event.trigger('SignalRClearBeforeLogout');
        this._authService.logout();
    }

    onMySettingsModalSaved(): void {
        this.shownLoginName = this.appSession.getShownLoginName();
    }

    onUserMenuOpenChange(isOpen: boolean): void {
        this.uparrow = isOpen;
    }

    backToMyAccount(): void {
        this._impersonationService.backToImpersonator();
    }

    switchToLinkedUser(linkedUser: LinkedUserDto): void {
        this._linkedAccountService.switchToAccount(linkedUser.id, linkedUser.tenantId);
    }

    downloadCollectedData(): void {
        this._profileServiceProxy.prepareCollectedData().subscribe(() => {
            this.message.success(this.l('GdprDataPrepareStartedNotification'));
        });
    }
    userFullname(name: string) {
        if(!name)return '-';
        let fullname = name.split(" ");
        name = fullname.length > 1 ?this.getTitleCase(fullname[0])+" "+this.getTitleCase(fullname[1]) : this.getTitleCase(fullname[0]);
         return name ;
     }
 
     // function which return titlecase string;
     getTitleCase(s : string){
        if(s[0] !==" " && s !== undefined)
        {
            let char = s ? s[0].toUpperCase() : '';
        return char + s.slice(1)?.toLowerCase();
        }         
            
        }
}
